import i18n from 'config/i18n';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { formatByLocale, fromISOString } from 'utils/date';
import { download } from 'utils/download';

import {
  GeneratedReport,
  getDownloadUrl,
  getGeneratedReports
} from 'RestAPIs/reports/generated';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead
} from '@mui/material';

import { Tooltip } from 'components/shared';
import { TableHeaderRow, ZebraStripedTableRow } from 'components/shared/Table';
import { Icon } from '@sdb/planning-ui';

const PRINT_KEYS = {
  reference_date: 'referenceDate',
  valid_from: 'startDate',
  valid_to: 'endDate',
  session_id: 'sessionId',
  status: 'type'
};

const PRINT_VALUES = {
  reference_date: (date) => formatByLocale(fromISOString(date)),
  valid_from: (date) => formatByLocale(fromISOString(date)),
  valid_to: (date) => formatByLocale(fromISOString(date)),
  status: (status) => (status === '-10' ? i18n.t('errors') : i18n.t('success'))
};

export default function GeneratedReports() {
  const { t } = useTranslation();

  const loadReport = (generatedReport: GeneratedReport) => {
    getDownloadUrl(generatedReport.fileId).then(({ data }) => {
      download(data, generatedReport.filename || 'report.xls');
    });
  };
  const generateParams = (params) =>
    Object.entries(params)
      .filter(([key]) => key in PRINT_KEYS)
      .map(
        ([key, value]) =>
          `${t(PRINT_KEYS[key])}:
         ${(PRINT_VALUES[key] || ((v) => v))(value)}`
      )
      .join(', ');

  const { data: generatedReports = [] } = useQuery(
    ['GeneratedReports'],
    async () => {
      const { data } = await getGeneratedReports();
      data.sort(
        (a, b) =>
          fromISOString(b.inserted).valueOf() -
          fromISOString(a.inserted).valueOf()
      );
      return data;
    }
  );

  return (
    <TableContainer>
      <Table sx={{ width: '100%' }} size="small">
        <TableHead>
          <TableHeaderRow>
            <TableCell>
              {t('report')}
              <Tooltip title={t('generatedReportsRetention') || ''}>
                <IconButton color="primary" size="small">
                  <Icon type="circle-info" fontSize="small" color="inherit" />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell>{t('parameters')}</TableCell>
            <TableCell>{t('requested')}</TableCell>
            <TableCell>{t('status')}</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableHeaderRow>
        </TableHead>
        <TableBody>
          {generatedReports.map((generatedReport) => (
            <ZebraStripedTableRow key={generatedReport.fileId}>
              <TableCell>
                {t(`generatedReport.${generatedReport.reportName}`)}
              </TableCell>
              <TableCell>
                {generateParams(generatedReport.parameters)}
              </TableCell>
              <TableCell>
                {formatByLocale(
                  fromISOString(generatedReport.inserted),
                  'PPpp'
                )}
              </TableCell>
              <TableCell>
                {t(`generatedReportStatus.${generatedReport.status}`)}
              </TableCell>
              <TableCell align="right">
                {generatedReport.status === 'DONE' &&
                  generatedReport.filename && (
                    <IconButton
                      onClick={() => loadReport(generatedReport)}
                      color="primary"
                      size="small"
                    >
                      <Icon type="download" fontSize="small" color="inherit" />
                    </IconButton>
                  )}
              </TableCell>
            </ZebraStripedTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
